<template>
  <b-container class="comment-list" fluid>
    <!-- アップロード中のローディング表示 -->
    <loading-upload v-if="isUploading" :msg="loadingMsg" />

    <comment-list-item
      v-for="comment in commentList"
      :key="comment.id"
      :id="comment.id"
      :user="comment.owner"
      :createdAt="comment.createdAt"
      :message="comment.message"
      :like="comment.like"
      :photos="comment.photos"
      :messageId="id"
      :users="users"
      :viewer="viewer"
      :deleted="comment.deleted"
      :deletedUser="comment.deletedUser"
      v-on:openModal="openModal"
      v-on:toEditComment="toEditComment"
      v-on:toDeleteComment="toDeleteComment"
    />

    <div
      class="position-relative"
      @dragover.prevent="drag = true"
      @dragleave.prevent="drag = false"
      v-on:drop.prevent="drag = false"
    >
      <drag-area-overlay :drag="drag" v-on:dropFiles="dropFiles" />
      <b-row v-if="commentAvailable" class="comment-form">
        <div class="input-form">
          <b-form-textarea
            ref="commentForm"
            class="input-box"
            max-rows="10"
            @blur="blurEvent"
            @focus="focusEvent"
            @keypress.enter.native="enableSubmit"
            @keyup.enter.native="enableSubmit"
            v-model="text"
            wrap="hard"
            aria-describedby="comment-feedback"
            placeholder="コメントを入力 (Enterで改行)"
          />
          <b-button class="send-button" :disabled="!contentState" v-on:click="addComment">
            <icon-enter :disabled="!contentState"></icon-enter>
          </b-button>
        </div>
      </b-row>
      <b-row v-if="text.length" no-gutters>
        <b-form-text id="comment-feedback" :class="contentState ? 'success' : 'warning'">
          {{ validCommentMessage }}
        </b-form-text>
      </b-row>
      <!-- 添付ファイルフォーム -->
      <file-selection-form
        v-if="commentAvailable"
        ref="fileSelectionForm"
        contentType="COMMENT"
        label=""
        class="comment-attachment"
        :multiple="true"
        :photos="[]"
        :messageId="id"
        :allow_attachment_type="allow_attachment_type"
        :file-number-limit="10"
        v-on:onUpdateThumb="updateFile"
      />
    </div>

    <delete-modal
      :id="deleteModalId"
      :messageId="id"
      :commentId="deleteId"
      :viewer="viewer"
      type="comment"
    />
  </b-container>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore } from "@/store";
import "./comment-list.scss";
import CommentListItem, { CommentListItemData } from "./CommentListItem.vue";
import IconEnter from "./icon/IconEnter.vue";
import { AttachmentFactory, Comment, User } from "../model";
import DeleteModal from "./DeleteModal.vue";
import { PhotoStreamArgs } from "./PhotoStream.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

import { v4 as uuidv4 } from "uuid";

import { ReactionController } from "../model/reaction-controller";
import { Attachment, IconReactionType } from "../API";
import LoadingUpload from "./loading/LoadingUpload.vue";
import AclManager from "../model/acl-manager";
import { allowFeature, FeatureName } from "../direct-app-config";
import FileSelectionForm from "./FileSelectionForm.vue";
import DragAreaOverlay from "./DropAreaOverlay.vue";
import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import { AttachmentFileTypesDefault, AttachmentFileTypesNone } from "@/suppport-attachment-types";
import S3AccessUtility from "./s3-acceess-utility";
import type { SORT_TYPE } from "@/store";

const COMMENT_CONTENT_MAX_LENGTH = 300;

export default defineComponent({
  name: "comment-list",
  components: {
    CommentListItem,
    IconEnter,
    DeleteModal,
    LoadingUpload,
    FileSelectionForm,
    DragAreaOverlay,
  },
  props: {
    // 親messageIdを引き継ぐ
    id: { type: String, default: () => "", required: true },
    comments: { type: Array as PropType<CommentListItemData[]>, default: () => [] },
    // 組織内ユーザー情報
    users: { type: Array as PropType<User[]>, default: () => [] },
    // ログインユーザ情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
    // Direct組織設定
    allow_attachment_type: {
      // 元 @Prop は runtime type 引数なし（型注釈のみ）。検証を増やさないため type は付けない。
      default: () => AttachmentFileTypesDefault as AttachmentFileTypes,
    },
    sort: { type: String as PropType<SORT_TYPE>, default: () => "ASC" },
  },
  setup(props, { emit }) {
    // 旧 TextProcessMixin → composable（1-D 利用側書き換え）
    const { getLength } = useTextProcess();
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $route / $scrollTo は Vue インスタンス経由（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const text = ref<string>("");
    const target = ref<FileList | null>(null);
    const sendFiles = ref<File[]>([]);
    const param = ref<{ messageId: string; commentId: string }>({ messageId: "", commentId: "" });

    // コメント編集画面への引数
    const modalCommentId = ref<string>("");
    const modalMessageId = ref<string>("");
    const modalMessage = ref<string>("");
    const modalPhotos = ref<Attachment[]>([]);

    // コメント削除画面への引数
    const deleteId = ref<string>("");

    const drag = ref<boolean>(false);
    const focus = ref<boolean>(false);

    const submitLock = ref<boolean>(false);
    const allow_submit = ref<boolean>(false);

    /* アップローディング関係 */
    const isUploading = ref<boolean>(false);
    const uploadFiles = ref<number>(0);
    const nowuploads = ref<number>(0);

    const fileTypeErrorMsg = ref<string>("下記の添付ファイルは対応しておりません。");

    // コメントIDが指定されていて scrollTo する場合の実行フラグ
    // true: スクロールジャンプ予定で、未だスクロールジャンプしていない
    let scrollJumpFlag = false;

    // $refs
    const commentForm = ref<Vue | null>(null);
    const fileSelectionForm = ref<InstanceType<typeof FileSelectionForm> | null>(null);

    // コメントリストのウォッチャー：URLにコメントID指定がある場合に利用する
    watch(
      () => props.comments,
      (val: CommentListItemData[]): void => {
        if (scrollJumpFlag == false) return; // 自動スクロールOFF
        if (!vm?.$route) return;
        const cmtId = vm.$route.params.commentId;
        if (!cmtId) return;
        const targetId = cmtId;

        // コメントID指定の場合の存在チェック
        const comment = val.find((comment) => comment.id == cmtId);
        if (!comment) return; // 次の更新待ち

        // 存在するのでスクロールジャンプ
        scrollJumpFlag = false;
        void nextTick(() => {
          const toId = `#cmt_${targetId}`;
          setTimeout(() => {
            vm?.$scrollTo(toId, 0, { offset: -10, container: "#msglist" });
          }, 1000);
        });
      },
      { deep: true, immediate: true }
    );

    // 改行されているかの判定
    const includesNewLine = computed((): boolean => {
      return new RegExp(/\r?\n/, "g").test(text.value);
    });

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type != AttachmentFileTypesNone;
    });

    // コメント一覧は作成順で取得する（話題や投稿とは順序が違う）
    const commentList = computed((): CommentListItemData[] => {
      const sorted = [...props.comments];
      const desc = props.sort === "DESC";

      if (desc) {
        sorted.sort((l, r) => r.createdAt.getTime() - l.createdAt.getTime());
      } else {
        sorted.sort((l, r) => l.createdAt.getTime() - r.createdAt.getTime());
      }

      const ctl = new ReactionController();
      sorted.forEach((comment) => {
        comment.like = ctl.getReaction(comment, IconReactionType.LIKE);
      });
      return sorted;
    });

    const editModalId = computed((): string => {
      return `comment-edit-modal-${props.id}`; // 投稿IDでモーダルをユニーク化
    });

    const deleteModalId = computed((): string => {
      return `delete-modal-${props.id}`; // 投稿IDでモーダルをユニーク化
    });

    const attachementLabelStyle = computed((): string => {
      return allow_attachment.value ? "cursor: pointer;" : "cursor: not-allowed;";
    });

    const contentState = computed((): boolean | null => {
      const trimmed = text.value.trim();
      if (!text.value) return false;
      // コメント入力にfocusされておらず、コメントが入力されてない時にバリデーションを外す
      if (!focus.value && getLength(trimmed) == 0) {
        return null;
      }
      // トリミング後の文字数が0より多く、入力文字数が規定数以下のときに送信可能
      else {
        return getLength(trimmed) > 0 && getLength(text.value) <= COMMENT_CONTENT_MAX_LENGTH;
      }
    });

    const validCommentMessage = computed((): string => {
      return `コメントは${COMMENT_CONTENT_MAX_LENGTH}文字以内です(${getLength(text.value)}/${COMMENT_CONTENT_MAX_LENGTH})`;
    });

    const showCameraButton = computed((): boolean => {
      return useAppStore().isAndroid;
    });

    const loadingMsg = computed((): string => {
      return `添付ファイルを送信中です (${nowuploads.value}/${uploadFiles.value})`;
    });

    // 権限
    const domainId = computed((): string => useAppStore().domainId);
    const topicId = computed((): string => useAppStore().topicId);
    const commentAvailable = computed((): boolean => {
      if (hasFeature("deny-edit") == false) return true; // 未対応組織なので許可
      return (
        AclManager.getCreateCommentAcl(AclManager.store, domainId.value, topicId.value, props.id) !=
        "deny"
      );
    });
    const reactionAvailable = computed((): boolean => {
      if (hasFeature("deny-edit") == false) return true; // 未対応組織なので許可
      return (
        AclManager.getCreateReactionAcl(
          AclManager.store,
          domainId.value,
          topicId.value,
          props.id
        ) != "deny"
      );
    });

    const blurEvent = (): void => {
      focus.value = false;
    };

    const focusEvent = (): void => {
      focus.value = true;
    };

    const openModal = (value: string): void => {
      const comment = props.comments.find((m) => m.id === value);
      if (!comment) return;
      const param: PhotoStreamArgs = {
        files: comment.photos,
        messageId: props.id,
        commentId: comment.id,
      };
      rootEvent.emit("open-photo-stream", param);
    };

    const updateFile = (files: (File | Attachment)[]): void => {
      const fileList: File[] = [];
      files.map((file) => {
        if (!AttachmentFactory.isAttachment(file)) {
          fileList.push(file);
        }
      });
      sendFiles.value = fileList;
    };

    const dropFiles = (files: FileList): void => {
      const form = fileSelectionForm.value as unknown as FileSelectionForm;
      form.dropFiles(files);
    };

    const addComment = async (): Promise<void> => {
      // 送信連打による連続送信を防止
      if (submitLock.value) return;
      submitLock.value = true;

      const newId = uuidv4();
      const form = fileSelectionForm.value as unknown as FileSelectionForm;
      const s3AccessUtility = S3AccessUtility.getInstance();
      isUploading.value = true;

      let files: Attachment[] = [];
      const generator = async () => {
        let returnValue: boolean = true;
        for await (const result of s3AccessUtility.uploadFilesGenerator(sendFiles.value, {
          domainId: domainId.value,
          topicId: topicId.value,
          messageId: props.id,
          commentId: newId,
        })) {
          if (!result) {
            returnValue = false;
            break;
          }

          files = result.attachments;
          nowuploads.value = result.uploaded;
          uploadFiles.value = result.total;
          if (result.done) {
            returnValue = true;
            break;
          }
        }
        return returnValue;
      };

      // アップロード
      const result = await generator();
      if (!result) {
        rootEvent.emit("show-error-modal", {
          msg: "添付ファイルの送信に失敗しました。",
          afterProcess: () => {
            return;
          },
        });
        return;
      }
      isUploading.value = false;
      nowuploads.value = 0;
      form.resetFileList();

      if (!files) {
        target.value = null;
        submitLock.value = false;
        return;
      }
      const comment = Comment.create({
        id: newId,
        message: text.value,
        photos: files,
      });

      const obj = { comment: comment, messageId: props.id };

      rootEvent.emit("on-comment-create", obj);

      emit("on-comment-create", obj);

      text.value = "";
      sendFiles.value = [];
      target.value = null;
      submitLock.value = false;
      allow_submit.value = false;
    };

    const enableSubmit = (): void => {
      allow_submit.value = true;
    };

    const enterSubmit = async (): Promise<void> => {
      if (!allow_submit.value) return;

      // enter送信用
      if (contentState.value) {
        await addComment();
      }
    };

    const toEditComment = (value: string): void => {
      const comment = props.comments.find((c) => c.id === value);
      if (comment) {
        const param = {
          commentId: comment.id,
          messageId: props.id,
          message: comment.message,
          photos: comment.photos,
        };
        // MessageList の CommenteditModal へ イベントを飛ばす
        rootEvent.emit("open-comment-edit-modal", param);
      }
    };

    const toDeleteComment = (value: string): void => {
      const comment = props.comments.find((c) => c.id === value);
      if (comment) {
        deleteId.value = comment.id;
        vm?.$bvModal.show(deleteModalId.value);
      }
    };

    /** 指定されたフィーチャーを持つかどうかを調べる */
    const hasFeature = (keyword: FeatureName): boolean => allowFeature(keyword);

    /******************************************************************/
    // Lifecycle Hook

    onMounted((): void => {
      // scroll処理(コメントID指定の場合のみ)
      if (vm?.$route) {
        const msgId = vm.$route.params.messageId;
        if (msgId && msgId == props.id) {
          const cmtId = vm.$route.params.commentId;
          if (cmtId) scrollJumpFlag = true; // スクロールジャンプ有りにする
        }
      }
    });

    return {
      text,
      target,
      sendFiles,
      param,
      modalCommentId,
      modalMessageId,
      modalMessage,
      modalPhotos,
      deleteId,
      drag,
      focus,
      submitLock,
      allow_submit,
      isUploading,
      uploadFiles,
      nowuploads,
      fileTypeErrorMsg,
      commentForm,
      fileSelectionForm,
      includesNewLine,
      allow_attachment,
      commentList,
      editModalId,
      deleteModalId,
      attachementLabelStyle,
      contentState,
      validCommentMessage,
      showCameraButton,
      loadingMsg,
      domainId,
      topicId,
      commentAvailable,
      reactionAvailable,
      blurEvent,
      focusEvent,
      openModal,
      updateFile,
      dropFiles,
      addComment,
      enableSubmit,
      enterSubmit,
      toEditComment,
      toDeleteComment,
      hasFeature,
    };
  },
});
</script>
